import { uploadsApi } from "@/features/uploads/api";
import { uploadFileToPresignedUrl } from "./upload";
import type { PhotoFile } from "./photo";

export interface UploadedPhoto {
  storageKey: string;
  publicUrl: string; // The public URL constructed from storageKey
}

/**
 * Constructs the public URL from a storage key
 * The storageKey is a path like "tasks/93/1764408553711-photo_1764408133384.jpeg"
 * We need to prepend the CDN/base URL to get the full public URL
 */
function getPublicUrl(storageKey: string): string {
  // If storageKey is already a full URL, return it as-is
  if (storageKey.startsWith('http://') || storageKey.startsWith('https://')) {
    return storageKey;
  }

  // Get the API base URL to construct the public URL
  // For now, use the same base as the API - this should ideally be a separate CDN URL
  const EXTERNAL_API_BASE = import.meta.env.VITE_EXTERNAL_API_BASE || "https://camilla-dev.eu.ngrok.io";
  
  // Construct the public URL by appending the storageKey to the base URL
  // The storageKey should be the path relative to the CDN root
  // Remove leading slash from storageKey if present to avoid double slashes
  const cleanStorageKey = storageKey.startsWith('/') ? storageKey.slice(1) : storageKey;
  return `${EXTERNAL_API_BASE}/${cleanStorageKey}`;
}

/**
 * Uploads a single photo to Cloudflare R2 and returns the public URL
 * @param photo - PhotoFile object to upload
 * @param taskId - Optional task ID if the upload is associated with a task
 * @returns Promise with public URL
 */
export async function uploadPhoto(
  photo: PhotoFile,
  taskId?: number | null
): Promise<string> {
  try {
    // Get presigned URL
    const presignResponse = await uploadsApi.getPresignedUrl(
      photo.file.name,
      photo.file.type,
      taskId
    );

    // Upload file to presigned URL
    await uploadFileToPresignedUrl(photo.file, presignResponse.presignedUrl);

    // Use publicUrl from response if available, otherwise construct from storageKey
    return presignResponse.publicUrl || getPublicUrl(presignResponse.storageKey);
  } catch (error) {
    console.error("Error uploading photo:", error);
    throw error;
  }
}

/**
 * Uploads multiple photos to Cloudflare R2 and returns their public URLs
 * @param photos - Array of PhotoFile objects to upload
 * @param taskId - Optional task ID if the uploads are associated with a task
 * @returns Promise with array of uploaded photo info (storageKey and publicUrl)
 */
export async function uploadPhotos(
  photos: PhotoFile[],
  taskId?: number | null
): Promise<UploadedPhoto[]> {
  if (photos.length === 0) {
    return [];
  }

  try {
    console.log("uploadPhotos: Starting upload for", photos.length, "photos");
    
    // Get presigned URLs for all photos
    const presignPromises = photos.map((photo) =>
      uploadsApi.getPresignedUrl(
        photo.file.name,
        photo.file.type,
        taskId
      )
    );

    const presignResponses = await Promise.all(presignPromises);
    console.log("uploadPhotos: Got presigned URLs:", presignResponses);

    // Upload all files to their presigned URLs
    const uploadPromises = presignResponses.map(async (presignResponse, index) => {
      console.log(`uploadPhotos: Uploading photo ${index + 1} to presigned URL`);
      try {
        await uploadFileToPresignedUrl(
          photos[index].file,
          presignResponse.presignedUrl
        );
        console.log(`uploadPhotos: Successfully uploaded photo ${index + 1}`);
        
        // Use publicUrl from response if available, otherwise construct from storageKey
        const publicUrl = presignResponse.publicUrl || getPublicUrl(presignResponse.storageKey);
        console.log(`uploadPhotos: Photo ${index + 1} public URL:`, publicUrl);
        
        return {
          storageKey: presignResponse.storageKey,
          publicUrl,
        };
      } catch (uploadError) {
        console.error(`uploadPhotos: Failed to upload photo ${index + 1}:`, uploadError);
        throw uploadError;
      }
    });

    const result = await Promise.all(uploadPromises);
    console.log("uploadPhotos: All photos uploaded successfully:", result);
    return result;
  } catch (error) {
    console.error("Error uploading photos:", error);
    throw error;
  }
}

